import { useQuery, useQueryClient } from "@tanstack/react-query";
import { get } from "@/shared/utils/fetchApi";
import type { MeContextDto } from "@/shared/types/meContext";
import { OrganizationService } from "@/features/Organization/services/organizationService";

export const ME_CONTEXT_KEY = ["auth", "me-context"] as const;

/**
 * Aggregate post-login bootstrap. Calls GET /auth/me/context (api-v2) and
 * hydrates React Query with each slot under canonical keys, so existing hooks
 * (useSidebarSubscription, useTourSettings, useUserSettingsByUserId, etc.)
 * read from cache without firing their own legacy requests.
 *
 * Sem gate: a flag `api-v2-auth` saiu na limpeza da Fase 9 (AB#81972), e este
 * agregado e o unico caminho — o fan-out legado que ele substituia apontava para
 * uma api que nao existe mais.
 */
export function useMeContext(opts: { enabled?: boolean } = {}) {
  const qc = useQueryClient();

  return useQuery({
    queryKey: ME_CONTEXT_KEY,
    enabled: opts.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const t0 = performance.now();
      const ctx = await get<MeContextDto>("/auth/me/context");
      const durationMs = Math.round(performance.now() - t0);

      // Canonical keys (planned single source going forward)
      qc.setQueryData(["auth", "me"], ctx.user);
      qc.setQueryData(["auth", "onboarding"], ctx.onboarding);
      qc.setQueryData(["organizations"], ctx.organizations);
      qc.setQueryData(["subscription"], ctx.subscription);
      qc.setQueryData(["feedback", "config"], ctx.feedbackConfig);
      qc.setQueryData(["settings", "tour_settings"], ctx.tourSettings);
      qc.setQueryData(["user_settings", ctx.user.id], ctx.userSettings);

      // Compat keys — match existing react-query hooks already in the codebase
      // so they pick up cache without rename. Remove after §2.7 cleanup.
      //
      // useTourSettings espera TourConfig já unwrapped (`SettingsService.getTourSettings`
      // faz `items.find(...).value as TourConfig`). Hidratar o array bruto faz
      // `config.steps.filter(...)` em tourService explodir com "undefined.filter".
      const tourEntry = ctx.tourSettings.find(
        (s) => s.type === "tour_settings",
      );
      const tourConfig = (tourEntry?.value as unknown) ?? null;
      if (tourConfig) {
        qc.setQueryData(["tour-settings"], tourConfig);
      }

      qc.setQueryData(["user_settings_by_user", ctx.user.id], ctx.userSettings);
      if (ctx.user.organizationId) {
        qc.setQueryData(
          ["sidebar-subscription", ctx.user.organizationId],
          ctx.subscription,
        );
      }

      // Pré-aquece o cache estático de OrganizationService.userProfileCache.
      // Evita que callers imperativos de getCurrentUserProfile (useOrganization,
      // OrganizationSettings, EditUserWorkspacesDialog, etc.) disparem
      // `/auth/me` próprio logo após o aggregate resolver.
      OrganizationService.primeProfileCacheFromMe(ctx.user);

      // Telemetry breadcrumb — structured so rollout dashboards can grep.
      const slotsFailed = ctx.failures?.map((f) => f.key) ?? [];
      console.info("[telemetry] me_context.bootstrap", {
        event: "me_context.bootstrap",
        duration_ms: durationMs,
        partial: ctx.partial,
        slots_failed: slotsFailed,
        slots_failed_count: slotsFailed.length,
        user_id: ctx.user.id,
        org_id: ctx.user.organizationId ?? null,
      });

      if (ctx.partial && ctx.failures?.length) {
        for (const f of ctx.failures) {
          console.warn("[telemetry] me_context.slot_failed", {
            event: "me_context.slot_failed",
            slot: f.key,
            code: f.code,
            message: f.message,
          });
        }
      }

      return ctx;
    },
  });
}
